<template>
	<b-modal ref="modal" size="lg" hide-footer>
		<template v-slot:modal-title>
			{{ $t('tiers.identification.reassign_titre') }}
		</template>

		<p>{{ $t('tiers.identification.reassign_explication') }}</p>

		<div v-if="identification" class="box">
			<div class="font-weight-bold">{{ identification.tiersident_label || $t('tiers.identification.sans_label') }}</div>
			<div>{{ identification.icd ? identification.icd.icd_label : '' }} : {{ identification.tiersident_value }}</div>
		</div>

		<h3 class="mt-4">{{ $t('tiers.identification.reassign_creer') }}</h3>
		<p>{{ $t('tiers.identification.reassign_creer_hint') }}</p>

		<div class="input-group">
			<input type="text" class="form-control" v-model="identifier" @keyup.enter="searchPappers">
			<div class="input-group-append">
				<button class="btn btn-secondary" :disabled="searching" @click="searchPappers">
					<font-awesome-icon v-if="searching" :icon="['fal', 'spinner']" spin />
					<font-awesome-icon v-else :icon="['fal', 'search']" />
				</button>
			</div>
		</div>

		<div v-if="searched && !pappers" class="alert alert-warning mt-3">
			{{ $t('tiers.identification.reassign_aucun_resultat') }}
		</div>

		<div v-if="pappers" class="box mt-3">
			<div class="font-weight-bold">{{ pappers.tiers_rs }}</div>
			<div>{{ pappers.tiers_address1 }}</div>
			<div v-if="pappers.tiers_address2">{{ pappers.tiers_address2 }}</div>
			<div>{{ pappers.tiers_postalcode }} {{ pappers.tiers_town }}</div>
		</div>

		<button v-if="pappers" class="btn btn-primary btn-block mt-3" :disabled="processing" @click="createTiers">
			{{ $t('tiers.identification.reassign_creer_bouton') }}
			<font-awesome-icon v-if="processing" :icon="['fal', 'spinner']" spin />
		</button>

		<h3 class="mt-4">{{ $t('tiers.identification.reassign_rattacher') }}</h3>
		<p>{{ $t('tiers.identification.reassign_rattacher_hint') }}</p>

		<e-select
			v-model="selected_tiers"
			track-by="tiers_id"
			label="tiers_rs"
			:placeholder="$t('tiers.rechercher')"
			:options="selectable_tiers"
			:loading="loading_tiers"
			:searchable="true"
			:allow-empty="true"
			:show-labels="false"
		>
			<template slot="noOptions">{{ $t('global.list_empty') }}</template>
		</e-select>

		<button class="btn btn-primary btn-block mt-3" :disabled="!selected_tiers || processing" @click="reassign">
			{{ $t('tiers.identification.reassign_rattacher_bouton') }}
			<font-awesome-icon v-if="processing" :icon="['fal', 'spinner']" spin />
		</button>
	</b-modal>
</template>


<script type="text/javascript">
	import Tiers from "@/mixins/Tiers.js"

	export default {
		name: "TiersIdentificationReassign",
		mixins: [Tiers],
		props: {
			tiers_id: { type: Number, required: true }
		},

		data() {
			return {
				identification: null,
				identifier: '',
				pappers: null,
				searching: false,
				searched: false,
				processing: false,
				loading_tiers: false,
				tiers: [],
				selected_tiers: null
			}
		},

		computed: {
			/**
			 * Le tiers dont on détache l'identifiant n'est pas une destination : l'y remettre ne
			 * déplacerait rien et laisserait croire à l'opérateur qu'il a agi.
			 */
			selectable_tiers() {
				return this.tiers.filter(tiers => tiers.tiers_id !== this.tiers_id)
			}
		},

		methods: {
			/**
			 * Ouvre la modale sur un identifiant : point d'entrée appelé par l'onglet. L'annuaire est
			 * interrogé d'emblée, l'identifiant à retrouver étant déjà connu.
			 *
			 * @param {object} identification Ligne tiers_identification à déplacer.
			 */
			async open(identification) {
				this.identification = identification
				// L'annuaire se cherche par identifiant quand la valeur en est un — neuf chiffres pour
				// l'entreprise, quatorze pour l'établissement qui facture — et par raison sociale sinon :
				// un numéro de TVA ou une adresse de routage n'y mène pas.
				const digits = String(identification.tiersident_value).replace(/\D/g, '')
				this.identifier = digits.length === 9 || digits.length === 14
					? digits
					: (identification.tiersident_label || '')
				this.pappers = null
				this.searched = false
				this.selected_tiers = null

				this.$refs.modal.show()

				this.loading_tiers = true
				this.tiers = await this.loadTiers()
				this.loading_tiers = false

				this.searchPappers()
			},

			async searchPappers() {
				if(!this.identifier) {
					return
				}

				this.searching = true
				this.pappers = null
				this.pappers = await this.searchPappersEntreprise(this.identifier)
				this.searched = true
				this.searching = false
			},

			/**
			 * Crée le fournisseur depuis l'annuaire puis lui transfère l'identifiant : c'est le cas où
			 * l'entreprise ne devait pas être fusionnée dans ce tiers et doit exister par elle-même.
			 */
			async createTiers() {
				this.processing = true

				try {
					const tiers_id = await this.createTiersFromPappers(this.pappers)

					this.tiers = await this.loadTiers()
					this.selected_tiers = this.tiers.find(tiers => tiers.tiers_id === tiers_id)

					if(!this.selected_tiers) {
						this.processing = false
						this.failureToast('toast.info_save_failed')
						return
					}

					await this.reassign()
				}
				catch(error) {
					this.processing = false
					console.error("IdentificationReassign::createTiers => ERROR", error)
					this.failureToast(typeof error === 'string' ? error : 'toast.info_save_failed')
				}
			},

			/**
			 * Transfère l'identifiant au tiers désigné. Les factures déjà reçues le suivent, de sorte
			 * que l'historique du fournisseur reste d'un seul tenant.
			 */
			async reassign() {
				this.processing = true

				const result = await this.reassignTiersIdentification(
					this.tiers_id,
					this.identification.tiersident_id,
					this.selected_tiers.tiers_id
				)

				this.processing = false

				if(!result) {
					this.failureToast('toast.info_modif_failed')
					return
				}

				this.successToast('toast.info_modif_succes')

				if(result.invoices > 0) {
					this.infoToast('tiers.identification.reassign_factures', { nb: result.invoices, tiers: result.tiers_rs })
				}

				this.$refs.modal.hide()
				this.$emit('submit')
			}
		}
	}
</script>
